import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const PublicFooter = () => {
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API}/public/company-info`);
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="mb-4">
              <img 
                src="/GTC_Logo.png" 
                alt="Global Trade Car" 
                className="h-14 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Your trusted partner for premium Japanese vehicles. Quality, reliability, and service you can depend on.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <a href="/" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Home
                </a>
              </li>
              <li>
                <a href="/vehicles" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Browse Vehicles
                </a>
              </li>
              <li>
                <a href="/about" className="text-sm text-gray-400 hover:text-white transition-colors">
                  About Us
                </a>
              </li>
              <li>
                <a href="/how-it-works" className="text-sm text-gray-400 hover:text-white transition-colors">
                  How It Works
                </a>
              </li>
              <li>
                <a href="/shipping" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Shipping Process
                </a>
              </li>
              <li>
                <a href="/arrival-schedule" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Arrival Schedule
                </a>
              </li>
              <li>
                <a href="/blog" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Blog
                </a>
              </li>
              <li>
                <a href="/contact" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Contact Us
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-lg font-bold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <a href="/terms" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Terms & Conditions
                </a>
              </li>
              <li>
                <a href="/privacy" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Privacy Policy
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              {/* Sales Contact */}
              <li className="flex items-start space-x-2 text-sm">
                <Phone className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-white font-medium">Sales</div>
                  <a 
                    href="tel:+817092702242"
                    className="text-gray-400 hover:text-white transition-colors block"
                  >
                    +81 70-9270-2242
                  </a>
                  <a 
                    href="mailto:sales@globaltradecar.com"
                    className="text-gray-400 hover:text-white transition-colors block"
                  >
                    sales@globaltradecar.com
                  </a>
                </div>
              </li>
              
              {/* Support Contact */}
              <li className="flex items-start space-x-2 text-sm">
                <Phone className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-white font-medium">Support</div>
                  <a 
                    href="tel:+817092702242"
                    className="text-gray-400 hover:text-white transition-colors block"
                  >
                    +81 70-9270-2242
                  </a>
                  <a 
                    href="mailto:support@globaltradecar.com"
                    className="text-gray-400 hover:text-white transition-colors block"
                  >
                    support@globaltradecar.com
                  </a>
                </div>
              </li>
              {companyInfo?.address && (
                <li className="flex items-start space-x-2 text-sm">
                  <MapPin className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-400">
                    {companyInfo.address}
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            {/* Social Links - LEFT */}
            <div className="flex items-center space-x-4 justify-center md:justify-start">
            <a href="https://facebook.com/globaltradecar" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Facebook">
              <Facebook className="h-5 w-5" />
            </a>
            <a href="https://instagram.com/globaltradecar" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram">
              <Instagram className="h-5 w-5" />
            </a>
            <a href="https://linkedin.com/company/globaltradecar" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="LinkedIn">
              <Linkedin className="h-5 w-5" />
            </a>
            <a href="https://x.com/globaltradecar" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="X (Twitter)">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            </div>
            
            {/* Copyright - CENTER */}
            <p className="text-sm text-gray-400 text-center">
              © 2025 Global Trade Car. All rights reserved.
            </p>
            
            {/* DZ Signature - RIGHT */}
            <p className="text-sm text-gray-400 text-center md:text-right font-semibold tracking-wider">
              DZ
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;